// ============================================================
// TERRAIN EDITS - what a finished build stroke or a palette edit does to
// the terrain.
//
// The build tools (useBattlefieldBuild) are plumbing between the pointer
// and these functions: each takes the terrain as it is and returns the
// terrain as it should be, so the rules below - erase takes everything,
// the last stroke of paint wins, a surface edit follows its paint - are
// testable without a pointer, a camera or React.
//
// Pure. Zero React dependency. Nothing here mutates its input.
// ============================================================
import type { Terrain } from './presets';
import type { Vec2 } from './boundary';
import { clearCells, setCells, columnTop, emptyGrid } from './voxels';
import type { Material, VoxelGrid } from './voxels';
import { dig, fillIn } from './excavation';
import { defaultSurfaces, eraseZone, paintZone, makeZone, zoneFromSurface, zoneWithSurface } from './zones';
import type { SurfaceDef, Zone } from './zones';
import { eraseKeys, digKeys, volumeKeys, zoneKeys } from './buildGesture';
import type { Cell2 } from './buildGesture';

/**
 * How deep an erase reaches when filling a trench back in, in cells.
 *
 * Deeper than anyone digs - a trench is measured in metres, not tens of
 * them - so "erase this" means the hole as well as what is standing in
 * it, without erase having to know how deep the hole was.
 */
const MAX_FILL_DEPTH = 64;

/** The build settings that decide which level a stroke works at. */
interface LevelRule {
  tool: string;
  level: number;
  autoStack: boolean;
  grid: VoxelGrid;
}

/**
 * Where a stroke starting at this cell builds (or erases).
 *
 * Dig always works below the ground. With auto-stack on, a stroke that
 * starts on top of something continues from there - which is what "stack
 * cubes" means when you say it out loud. Otherwise the level is literal.
 */
export function strokeLevel(rule: LevelRule, cell: Cell2, erasing: boolean): number {
  if (rule.tool === 'dig') return Math.min(rule.level, -1);
  if (!rule.autoStack) return rule.level;
  const top = columnTop(rule.grid, cell.x, cell.z);
  // Stacking means "on top of what is there" when building and "the top
  // thing that IS there" when taking away. Using the same level for both
  // aims erase one cube into thin air, which reads as the click having
  // done nothing at all.
  if (erasing) return top > 0 ? Math.max(0, top - 1) : rule.level;
  return Math.max(rule.level, top);
}

/** A finished drag, reduced to what the terrain edit needs. */
interface Stroke {
  footprint: Cell2[];
  erasing: boolean;
  level: number;
  layers: number;
}

/** Everything a stroke may read besides itself. */
export interface StrokeContext {
  terrain: Terrain;
  grid: VoxelGrid;
  /** The material key cubes are built from. */
  material: string;
  /** The level erase aims at in one column - `strokeLevel(rule, cell, true)`. */
  eraseLevel: (cell: Cell2) => number;
  /**
   * The layer count the erase TOOL reaches through. The build panel's
   * current value rather than the stroke's: the two only differ if the
   * field is edited mid-drag, and erase has always read the panel.
   */
  layers: number;
  /** The zone tool's surface key and typed name ('' for the surface's own). */
  zonePreset: string;
  zoneLabel: string;
}

/** Every zone with these cells taken out; a zone left with none is gone. */
export function carveZones(zones: Zone[], keys: string[]): Zone[] {
  return zones.map((zone) => eraseZone(zone, keys)).filter((zone) => zone.cells.length > 0);
}

/** The surface a key names, or the palette's first when it names none. */
export function pickSurface(palette: Record<string, SurfaceDef>, key: string): SurfaceDef {
  return palette[key] || Object.values(palette)[0];
}

/**
 * Paint these cells with a surface under a name.
 *
 * THE LAST STROKE WINS (v8.310.8). Ground is painted, not stacked: whatever
 * was under these cells gives them up, so no two zones ever overlap and
 * nothing has to decide whose traits, cover or description apply.
 *
 * One zone per NAME AND SURFACE, so painting more of the same place grows
 * it rather than littering the map with one-cell objects. Both, not the
 * name alone: the name box is not cleared when the surface changes, so
 * "Rubble" then "Regeneration" under one name used to join the FIRST zone
 * - keeping its surface, its colour and its empty traits - which is how a
 * healing floor healed nobody (the playtest, v8.310.8).
 */
export function paintZoneStroke(zones: Zone[], keys: string[], surface: SurfaceDef, label: string): Zone[] {
  const carved = carveZones(zones, keys);
  const existing = carved.find((zone) => zone.label === label && zone.surfaceKey === surface.key);
  if (existing) return carved.map((zone) => (zone === existing ? paintZone(zone, keys) : zone));
  return [...carved, paintZone(makeZone(zoneFromSurface(surface, label)), keys)];
}

/** The zone tool: paint, or with the other button take the paint away. */
function zoneStroke(ctx: StrokeContext, stroke: Stroke): Terrain {
  const keys = zoneKeys(stroke.footprint);
  const zones = ctx.terrain.zones || [];
  if (stroke.erasing) return { ...ctx.terrain, zones: carveZones(zones, keys) };
  const surface = pickSurface(ctx.terrain.surfaces || defaultSurfaces(), ctx.zonePreset);
  const label = ctx.zoneLabel.trim() || surface.label;
  return { ...ctx.terrain, zones: paintZoneStroke(zones, keys, surface, label) };
}

/**
 * ERASE TAKES AWAY WHATEVER IS THERE: cubes off the top of each column,
 * painted ground outright (ground has no depth to work through), and
 * TRENCHES TOO. Erase used to leave them, on the argument that filling one
 * in by accident costs real work - and then the rule "erase takes away
 * what you put down, except that" turned out to be a rule nobody could
 * hold. One tool, everything.
 *
 * Null when there was nothing there to take.
 */
function eraseStroke(ctx: StrokeContext, stroke: Stroke): Terrain | null {
  const { terrain, grid } = ctx;
  const { footprint } = stroke;
  const keys = footprint.flatMap((cell) => eraseKeys([cell], ctx.eraseLevel(cell), ctx.layers));
  const cleared = clearCells(grid, keys);
  const filled = footprint.flatMap((cell) => digKeys([cell], -1, MAX_FILL_DEPTH));
  const dug = fillIn(cleared.dug, filled);
  const voxels = dug === cleared.dug ? cleared : { ...cleared, dug };
  const before = terrain.zones || [];
  const zones = carveZones(before, zoneKeys(footprint));
  const changedZones = zones.length !== before.length || zones.some((z, i) => z !== before[i]);
  if (voxels === grid && !changedZones) return null;
  return { ...terrain, voxels, ...(changedZones ? { zones } : {}) };
}

/** Dig cuts a trench; with the other button it fills one back in. */
function digStroke(ctx: StrokeContext, stroke: Stroke): Terrain {
  const keys = digKeys(stroke.footprint, stroke.level, stroke.layers);
  const dug = stroke.erasing ? fillIn(ctx.grid.dug, keys) : dig(ctx.grid.dug, keys, ctx.material);
  return { ...ctx.terrain, voxels: { ...ctx.grid, dug } };
}

/**
 * Fill builds cubes; with the other button it takes them.
 *
 * PER CELL, not per drag. With stacking on, each column starts from its
 * own top - otherwise the whole rectangle takes its level from whichever
 * cell the pointer happened to go down on, and a drag that begins off a
 * platform erases the wrong layer of it while leaving the platform
 * standing.
 */
function cubeStroke(ctx: StrokeContext, stroke: Stroke): Terrain {
  const { erasing, level, layers } = stroke;
  const keys = stroke.footprint.flatMap((cell) =>
    erasing ? eraseKeys([cell], ctx.eraseLevel(cell), layers) : volumeKeys([cell], level, layers),
  );
  const voxels = erasing ? clearCells(ctx.grid, keys) : setCells(ctx.grid, keys, ctx.material);
  return { ...ctx.terrain, voxels };
}

const STROKES: Record<string, (ctx: StrokeContext, stroke: Stroke) => Terrain | null> = {
  zone: zoneStroke,
  erase: eraseStroke,
  dig: digStroke,
};

/**
 * The terrain after a finished stroke of this tool, or null when it changed
 * nothing. Any tool without its own rule (fill, structure) builds cubes -
 * or erases them on a right-drag, the one shortcut every tool shares.
 */
export function strokeTerrain(tool: string, ctx: StrokeContext, stroke: Stroke): Terrain | null {
  return (STROKES[tool] || cubeStroke)(ctx, stroke);
}

// ------------------------------------------------------------
// The palette: materials and surfaces, carried on the map.
// ------------------------------------------------------------

/** The terrain with its grid's material palette replaced. */
export function withMaterials(terrain: Terrain, grid: VoxelGrid, materials: Record<string, Material>): Terrain {
  return { ...terrain, voxels: { ...grid, materials } };
}

/** A fresh material under the first free `custom-<n>` key. */
export function newMaterial(materials: Record<string, Material>): Material {
  let n = 1;
  while (materials[`custom-${n}`]) n += 1;
  return { key: `custom-${n}`, name: `Material ${n}`, colour: '#8a8f98', hardness: 5, transparency: 0 };
}

/** A fresh surface under this key, named with the first free `Surface <n>`. */
export function newSurface(surfaces: Record<string, SurfaceDef>, key: string): SurfaceDef {
  let n = 1;
  while (Object.values(surfaces).some((s) => s.label === `Surface ${n}`)) n += 1;
  return { key, label: `Surface ${n}`, colour: '#a16207', going: 'difficult', hazard: false };
}

/**
 * A palette without one key, and the key to select instead - or null for
 * both when that was the last entry, which is never deleted: the tools
 * always need something to build or paint with.
 */
export function withoutEntry<T>(
  palette: Record<string, T>,
  key: string,
): { rest: Record<string, T>; fallback: string } | null {
  const rest = { ...palette };
  delete rest[key];
  const fallback = Object.keys(rest)[0];
  return fallback ? { rest, fallback } : null;
}

/**
 * A zone restyled by a new definition of its surface. A name a GM typed is
 * theirs and survives; one that merely defaulted to the surface's (it
 * equals `wasLabel`) follows the surface when it is renamed.
 */
function restyled(zone: Zone, next: SurfaceDef, wasLabel: string | undefined): Zone {
  // A legacy zone with no label and no surface label to follow keeps none,
  // as it always has - hence undefined through a string parameter.
  const label = zone.label === wasLabel ? next.label : zone.label;
  return zoneWithSurface(zone, next, label as string);
}

/**
 * Every painted zone that came from this surface, restyled by its edit.
 *
 * Zones follow their paint: "make the mire blind people too" should change
 * the mire already on the map, not only the next one painted. BY KEY,
 * falling back to the label for ground painted before zones carried one.
 * Matching on the label alone meant a named area - "the Chapel steps",
 * painted from "Rubble" - matched nothing, so edits reached the palette
 * and never the map.
 */
export function zonesAfterSurfaceEdit(zones: Zone[], surfaces: Record<string, SurfaceDef>, next: SurfaceDef): Zone[] {
  const wasLabel = surfaces[next.key]?.label;
  return zones.map((zone) => {
    const mine = zone.surfaceKey ? zone.surfaceKey === next.key : zone.label === wasLabel;
    return mine ? restyled(zone, next, wasLabel) : zone;
  });
}

/**
 * Every painted zone restyled by an imported palette - incoming wins on a
 * key collision, and colliding surfaces restyle the ground already painted
 * with them exactly as a surface edit would.
 */
export function zonesAfterSurfaceImport(
  zones: Zone[],
  surfaces: Record<string, SurfaceDef>,
  incoming: Record<string, SurfaceDef>,
): Zone[] {
  return zones.map((zone) => {
    const next = zone.surfaceKey
      ? incoming[zone.surfaceKey]
      : Object.values(incoming).find((sd) => surfaces[sd.key]?.label === zone.label);
    return next ? restyled(zone, next, surfaces[next.key]?.label) : zone;
  });
}

/**
 * Whether a drawn ring can become an arena: three corners at least and an
 * area of a square metre or more - a sliver arena would make every clamp
 * and every containment answer baffling.
 */
export function isDrawableRing(points: Vec2[] | null): boolean {
  if (!points || points.length < 3) return false;
  let area = 0;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    area += (points[j].x + points[i].x) * (points[j].z - points[i].z);
  }
  return Math.abs(area / 2) >= 1;
}

/** The terrain with nothing on it: no solids, no paint, no cubes. */
export function clearedTerrain(terrain: Terrain, tile: number): Terrain {
  return { ...terrain, solids: [], zones: [], voxels: emptyGrid(tile) };
}
